import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Star, Home, Clock, AlertCircle, CheckCircle,
  XCircle, Package, FlaskConical, Tag, Edit2,
} from 'lucide-react';
import { StatusBadge } from '../components/StatusBadge';
import { useFetch } from '../hooks';
import { labTestService } from '../services/api';
import { formatCurrency, formatDate } from '../utils/helpers';
import { getSampleMeta } from './LabTests';

/* ── Info Row ───────────────────────────────────────────────────────────────── */
const InfoRow = ({ label, value, icon: Icon, iconClass = 'text-gray-400' }) => (
  <div className="flex items-start gap-3 py-2.5 border-b border-gray-100 dark:border-gray-700/50 last:border-0">
    {Icon && <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${iconClass}`} />}
    <div className="flex-1 min-w-0">
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
      <p className="text-sm font-medium text-gray-900 dark:text-white mt-0.5">{value}</p>
    </div>
  </div>
);

/* ── Section Card ───────────────────────────────────────────────────────────── */
const Section = ({ title, icon: Icon, iconClass = 'text-blue-500', children }) => (
  <div className="card overflow-hidden">
    <div className="flex items-center gap-2.5 px-5 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30">
      <Icon className={`w-4 h-4 ${iconClass}`} />
      <h3 className="font-semibold text-gray-900 dark:text-white text-sm">{title}</h3>
    </div>
    <div className="px-5 py-1">{children}</div>
  </div>
);

/* ── Page ───────────────────────────────────────────────────────────────────── */
const LabTestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: test, loading } = useFetch(() => labTestService.getById(id), [id]);
  const { data: allTests } = useFetch(() => labTestService.getAll());

  if (loading) return (
    <div className="space-y-4">
      <div className="h-8 w-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {Array(3).fill(0).map((_, i) => <div key={i} className="card h-48 animate-pulse bg-gray-200 dark:bg-gray-700 rounded-xl" />)}
      </div>
    </div>
  );

  if (!test) return (
    <div className="text-center py-20">
      <p className="text-gray-500">Lab test not found.</p>
      <button onClick={() => navigate('/lab-tests')} className="btn-primary mt-4">Back to Lab Tests</button>
    </div>
  );

  const sampleMeta = getSampleMeta(test.sampleType);
  const SampleIcon = sampleMeta.icon;
  const packageTestDetails = allTests?.filter(t => (test.packageTests || []).includes(t.id)) || [];
  const discountedPrice = test.discount > 0 ? test.price * (1 - test.discount / 100) : null;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/lab-tests')} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-500">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white truncate">{test.name}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Lab Test Details</p>
        </div>
        <button onClick={() => navigate('/lab-tests')} className="btn-secondary flex items-center gap-1.5 text-sm">
          <Edit2 className="w-4 h-4" />Edit
        </button>
      </div>

      {/* Hero card */}
      <div className="card p-6">
        <div className="flex flex-col sm:flex-row gap-5 items-start">
          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center shrink-0 ${sampleMeta.bg}`}>
            <SampleIcon className={`w-8 h-8 ${sampleMeta.color}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">{test.name}</h2>
                  {test.featured && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded-full text-xs font-semibold">
                      <Star className="w-3 h-3 fill-yellow-500" />Featured
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{test.category}</p>
              </div>
              <StatusBadge status={test.status} />
            </div>

            {/* Price */}
            <div className="flex items-baseline gap-3 mt-3">
              <span className="text-3xl font-bold text-gray-900 dark:text-white">
                {discountedPrice ? formatCurrency(discountedPrice) : formatCurrency(test.price)}
              </span>
              {discountedPrice && (
                <>
                  <span className="text-lg text-gray-400 line-through">{formatCurrency(test.price)}</span>
                  <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-xs font-bold">{test.discount}% OFF</span>
                </>
              )}
            </div>

            {/* Quick badges */}
            <div className="flex flex-wrap gap-2 mt-3">
              {test.fastingRequired && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded-full text-xs font-semibold">
                  <AlertCircle className="w-3.5 h-3.5" />Fasting Required
                </span>
              )}
              {test.homeCollection && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-xs font-semibold">
                  <Home className="w-3.5 h-3.5" />Home Collection Available
                </span>
              )}
              {packageTestDetails.length > 0 && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs font-semibold">
                  <Package className="w-3.5 h-3.5" />Package ({packageTestDetails.length} tests)
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Detail sections grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* 1. Overview */}
        <Section title="Overview" icon={Tag} iconClass="text-blue-500">
          <InfoRow label="Test Name"  value={test.name} />
          <InfoRow label="Category"   value={test.category} icon={FlaskConical} iconClass="text-teal-500" />
          <InfoRow label="Price"      value={formatCurrency(test.price)} />
          {test.discount > 0 && <InfoRow label="Discount" value={`${test.discount}% — Final: ${formatCurrency(discountedPrice)}`} />}
          <InfoRow label="Status"     value={<StatusBadge status={test.status} />} />
          <InfoRow label="Added On"   value={test.createdAt ? formatDate(test.createdAt) : '—'} />
        </Section>

        {/* 2. Sample Info */}
        <Section title="Sample Information" icon={SampleIcon} iconClass={sampleMeta.color}>
          <InfoRow
            label="Required Sample Type"
            value={
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${sampleMeta.bg} ${sampleMeta.color}`}>
                <SampleIcon className="w-3.5 h-3.5" />{test.sampleType}
              </span>
            }
          />
          <InfoRow
            label="Fasting Required"
            value={
              test.fastingRequired
                ? <span className="flex items-center gap-1.5 text-orange-600 dark:text-orange-400 font-semibold text-sm"><CheckCircle className="w-4 h-4" />Yes — Patient must fast</span>
                : <span className="flex items-center gap-1.5 text-green-600 dark:text-green-400 font-semibold text-sm"><XCircle className="w-4 h-4" />No fasting required</span>
            }
          />
        </Section>

        {/* 3. Timing Info */}
        <Section title="Timing Information" icon={Clock} iconClass="text-purple-500">
          <InfoRow label="Report Delivery Time" value={test.reportTime} icon={Clock} iconClass="text-purple-400" />
          {test.timeSlots?.length > 0 ? (
            <div className="py-2.5">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Available Time Slots</p>
              <div className="flex flex-wrap gap-1.5">
                {test.timeSlots.map(slot => (
                  <span key={slot} className="px-2.5 py-1 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 rounded-lg text-xs font-medium border border-purple-200 dark:border-purple-800">
                    {slot}
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <InfoRow label="Available Time Slots" value="No specific slots assigned" />
          )}
        </Section>

        {/* 4. Additional Info */}
        <Section title="Additional Information" icon={Home} iconClass="text-green-500">
          <InfoRow
            label="Home Collection"
            value={
              test.homeCollection
                ? <span className="flex items-center gap-1.5 text-green-600 dark:text-green-400 font-semibold text-sm"><CheckCircle className="w-4 h-4" />Available</span>
                : <span className="flex items-center gap-1.5 text-gray-500 font-semibold text-sm"><XCircle className="w-4 h-4" />Not available</span>
            }
          />
          <InfoRow label="Featured" value={test.featured ? 'Yes — Shown in featured section' : 'No'} />
          {test.description && (
            <div className="py-2.5">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Description</p>
              <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{test.description}</p>
            </div>
          )}
        </Section>
      </div>

      {/* Package Tests */}
      {packageTestDetails.length > 0 && (
        <div className="card overflow-hidden">
          <div className="flex items-center gap-2.5 px-5 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30">
            <Package className="w-4 h-4 text-blue-500" />
            <h3 className="font-semibold text-gray-900 dark:text-white text-sm">Package Includes ({packageTestDetails.length} Tests)</h3>
          </div>
          <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {packageTestDetails.map(t => {
              const meta = getSampleMeta(t.sampleType);
              const Icon = meta.icon;
              return (
                <div key={t.id} className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-xl hover:border-blue-300 dark:hover:border-blue-600 transition-colors">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${meta.bg}`}>
                    <Icon className={`w-4 h-4 ${meta.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{t.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{formatCurrency(t.price)} · {t.reportTime}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default LabTestDetail;
